import { DocumentNode } from 'graphql';
import { client as apolloClient } from './apolloClient';

export interface GraphQLOptions {
  query: DocumentNode | string;
  variables?: Record<string, any>;
  authMode?: string;
}

export interface GraphQLResult<T = any> {
  data?: T;
  errors?: any[];
}

export interface GraphQLQuery<T> {
  __generatedQuery: T;
}

/**
 * Apollo Client API wrapper for GraphQL operations
 * Provides a unified interface for queries and mutations
 */
class ApolloAPI {
  async graphql<T = any>(
    options: GraphQLOptions & { __generatedQuery?: T }
  ): Promise<GraphQLResult<T extends GraphQLQuery<infer R> ? R : T>> {
    const isQuery =
      typeof options.query === 'string'
        ? options.query.trim().startsWith('query')
        : options.query.definitions?.[0]?.kind === 'OperationDefinition' &&
          (options.query.definitions[0] as any).operation === 'query';

    try {
      if (isQuery) {
        const result = await apolloClient.query({
          query: options.query as DocumentNode,
          variables: options.variables,
          fetchPolicy: 'network-only',
        });
        return {
          data: result.data as any,
          errors: result.errors ? [...result.errors] : undefined,
        };
      } else {
        const result = await apolloClient.mutate({
          mutation: options.query as DocumentNode,
          variables: options.variables,
        });
        return {
          data: result.data as any,
          errors: result.errors ? [...result.errors] : undefined,
        };
      }
    } catch (error: any) {
      // Handle Apollo Client errors
      if (error.graphQLErrors) {
        return {
          data: undefined,
          errors: error.graphQLErrors,
        };
      }
      if (error.networkError) {
        return {
          data: undefined,
          errors: [error.networkError],
        };
      }
      return {
        data: undefined,
        errors: [error],
      };
    }
  }
}

// Export as a singleton for consistent usage pattern
export const API = new ApolloAPI();
